import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Spinner, Alert, Button, Form } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { API_BASE, IMAGE_BASE } from "../Config";

const ReviewsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [reviews, setReviews] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [reviewTypeId, setReviewTypeId] = useState<string | null>(null);

  useEffect(() => {
    fetchReviewTypeId();
  }, []);

  useEffect(() => {
    if (reviewTypeId) {
      fetchReviews();
    }
  }, [reviewTypeId, page]);

  const fetchReviewTypeId = async () => {
    try {
      setLoading(true);
      
      // Get content type ID for reviews
      const contentTypesResponse = await fetch(`${API_BASE}/contenttypes`);
      if (!contentTypesResponse.ok) throw new Error("Failed to fetch content types");
      
      const contentTypes = await contentTypesResponse.json();
      // Find the review content type by name
      const reviewType = contentTypes.find((type: any) => type.name.toLowerCase() === "review");
      
      if (!reviewType) {
        setReviews([]);
        throw new Error("Review content type not found");
      }
      
      setReviewTypeId(reviewType._id);
    } catch (err) {
      console.error(err);
      setError("Failed to load reviews");
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      if (!reviewTypeId) return;
      
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      // Fetch reviews by type ID
      const response = await fetch(
        `${API_BASE}/contents?contentType=${reviewTypeId}&isActive=true&limit=6&page=${page}&sort=-createdAt`
      );
      
      if (!response.ok) throw new Error("Failed to fetch reviews");
      
      const data = await response.json();
      
      if (page === 1) {
        setReviews(data.contents || []);
      } else {
        setReviews(prev => [...prev, ...(data.contents || [])]);
      }
      
      // Check if there are more pages
      setHasMore(data.currentPage < data.totalPages);
    } catch (err) {
      console.error(err);
      setError("Failed to load reviews");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Fetch reviews by type ID and search query
      const response = await fetch(
        `${API_BASE}/contents?contentType=${reviewTypeId}&isActive=true&search=${encodeURIComponent(searchQuery)}`
      );
      
      if (!response.ok) throw new Error("Failed to search reviews");
      
      const data = await response.json();
      setReviews(data.contents || []);
      setHasMore(false);
    } catch (err) {
      console.error(err);
      setError("Failed to search reviews");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleReviewClick = (id: string) => {
    navigate(`/article/${id}`);
  };

  return (
    <div style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', minHeight: '100vh' }}>
      <Container className="py-5">
        {/* Modern Header */}
        <div className="text-center mb-5">
          <div className="d-inline-flex align-items-center mb-3">
            <div 
              className="rounded-circle d-flex align-items-center justify-content-center me-3"
              style={{ width: '60px', height: '60px', background: 'linear-gradient(135deg, #17a2b8 0%, #0062cc 100%)' }}
            >
              <i className="bi bi-star-half text-white" style={{ fontSize: '1.5rem' }}></i>
            </div>
            <div>
              <h1 className="display-5 fw-bold mb-0" style={{ color: '#0062cc' }}>Reviews</h1>
              <p className="text-muted mb-0">Read reviews and critiques of performances</p>
            </div>
          </div>
        </div>
        
        {/* Modern Search Section */}
        <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '20px' }}>
          <Card.Body className="p-4">
            <Row className="justify-content-center">
              <Col lg={8}>
                <div className="position-relative">
                  <Form.Control
                    type="text"
                    placeholder="Search reviews by title or content..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="ps-5 py-3 border-0 bg-light"
                    style={{ borderRadius: '50px', fontSize: '16px' }}
                  />
                  <i 
                    className="bi bi-search position-absolute" 
                    style={{ left: '20px', top: '50%', transform: 'translateY(-50%)', color: '#0062cc' }}
                  ></i>
                  <Button 
                    onClick={handleSearch} 
                    disabled={loading}
                    className="position-absolute border-0"
                    style={{ 
                      right: '8px', 
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      backgroundColor: '#17a2b8',
                      color: 'white',
                      borderRadius: '50px',
                      width: '40px',
                      height: '40px'
                    }}
                  >
                    {loading ? <Spinner size="sm" /> : <i className="bi bi-search"></i>}
                  </Button>
                </div>
              </Col>
            </Row>
            
            {/* Active Search Pill */}
            {searchQuery && (
              <div className="mt-3 d-flex justify-content-center">
                <span 
                  className="badge d-flex align-items-center"
                  style={{ backgroundColor: '#0062cc', borderRadius: '50px', padding: '8px 12px' }}
                >
                  Search: "{searchQuery}"
                  <button 
                    className="btn-close btn-close-white ms-2" 
                    style={{ fontSize: '10px' }}
                    onClick={() => {
                      setSearchQuery("");
                      fetchReviews();
                    }}
                  ></button>
                </span>
              </div>
            )}
          </Card.Body>
        </Card>
        
        {/* Results Header */}
        {!loading && (
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h5 className="mb-1" style={{ color: '#0062cc' }}>
                {reviews.length > 0 ? `${reviews.length} Reviews Found` : "No Reviews Found"}
              </h5>
              <small className="text-muted">
                {searchQuery && `Searching for "${searchQuery}"`}
              </small>
            </div>
          </div>
        )}

      {error && (
          <Alert 
            variant="" 
            className="border-0 text-center mb-4"
            style={{ 
              backgroundColor: 'rgba(23, 162, 184, 0.1)', 
              color: '#0062cc',
              borderRadius: '15px',
              padding: '20px'
            }}
          >
            <i className="bi bi-exclamation-triangle-fill me-2" style={{ color: '#17a2b8' }}></i>
            {error}
          </Alert>
        )}

      {/* Modern Reviews Grid */}
        <Row className="g-4">
          {!loading && reviews.map((item) => (
            <Col key={item._id} lg={6} md={6}>
              <Card 
                className="h-100 border-0" 
                onClick={() => handleReviewClick(item._id)}
                style={{ 
                  cursor: "pointer", 
                  borderRadius: '20px',
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  boxShadow: '0 4px 20px rgba(0, 98, 204, 0.1)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-8px) scale(1.02)";
                  e.currentTarget.style.boxShadow = "0 20px 40px rgba(0, 98, 204, 0.2)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0) scale(1)";
                  e.currentTarget.style.boxShadow = "0 4px 20px rgba(0, 98, 204, 0.1)";
                }}
              >
                <Card.Body className="d-flex flex-column p-4">
                  <div className="d-flex justify-content-between mb-3">
                    <Card.Title className="fw-bold" style={{ color: '#0062cc', fontSize: '1.1rem' }}>
                      {item.title}
                    </Card.Title>
                    <div className="text-warning">
                      <i className="bi bi-star-fill"></i>
                      <i className="bi bi-star-fill"></i>
                      <i className="bi bi-star-fill"></i>
                      <i className="bi bi-star-fill"></i>
                      <i className="bi bi-star-half"></i>
                    </div>
                  </div>
                  
                  <Card.Text className="text-muted flex-grow-1" style={{ fontSize: "0.9rem" }}>
                    {item.content && item.content.length > 150 
                      ? `${item.content.substring(0, 150).replace(/<[^>]*>/g, '')}...` 
                      : item.content?.replace(/<[^>]*>/g, '') || "Click to read this review."}
                  </Card.Text>
                  
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    {/* Writer Pill */}
                    {item.writer && (
                      <span 
                        className="badge d-inline-flex align-items-center"
                        style={{ 
                          backgroundColor: 'rgba(0, 98, 204, 0.1)', 
                          color: '#0062cc', 
                          borderRadius: '50px',
                          padding: '6px 12px',
                          fontSize: '0.8rem'
                        }}
                      >
                        <i className="bi bi-person me-1"></i>
                        {item.writer}
                      </span>
                    )}
                    
                    {/* Action Button */}
                    <small style={{ color: '#17a2b8', fontWeight: '600' }}>
                      Read Full Review <i className="bi bi-arrow-right ms-1"></i>
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
        
        {/* Loading Spinner */}
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" style={{ color: '#0062cc' }} />
            <p className="mt-3 text-muted">Loading reviews...</p>
          </div>
        )}
        
        {/* Modern Empty State */}
        {!loading && reviews.length === 0 && !error && (
          <Card className="border-0 text-center py-5" style={{ borderRadius: '20px', background: 'rgba(255, 255, 255, 0.8)' }}>
            <Card.Body className="py-5">
              <div 
                className="rounded-circle d-inline-flex align-items-center justify-content-center mb-4"
                style={{ 
                  width: '80px', 
                  height: '80px', 
                  background: 'rgba(23, 162, 184, 0.2)'
                }}
              >
                <i className="bi bi-star-half" style={{ fontSize: '2rem', color: '#0062cc' }}></i>
              </div>
              <h4 className="mb-3" style={{ color: '#0062cc' }}>No Reviews Found</h4>
              <p className="text-muted mb-4" style={{ maxWidth: '400px', margin: '0 auto' }}>
                {searchQuery 
                  ? "We couldn't find any reviews matching your search. Try adjusting your criteria." 
                  : "Check back soon for new performance reviews!"}
              </p>
              {searchQuery && (
                <Button 
                  onClick={() => {
                    setSearchQuery("");
                    fetchReviews();
                  }}
                  className="rounded-pill border-0 px-4 py-2"
                  style={{ backgroundColor: '#17a2b8', color: 'white', fontWeight: '600' }}
                >
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Clear Search
                </Button>
              )}
            </Card.Body>
          </Card>
        )}
      
      {/* Modern Load More Button */}
        {reviews.length > 0 && hasMore && !searchQuery && (
          <div className="text-center mt-5">
            <Button 
              onClick={() => setPage(p => p + 1)} 
              disabled={loadingMore}
              className="rounded-pill border-0 px-5 py-3"
              style={{ 
                background: 'linear-gradient(135deg, #17a2b8 0%, #0062cc 100%)',
                color: 'white',
                fontWeight: '600',
                fontSize: '16px',
                boxShadow: '0 4px 15px rgba(0, 98, 204, 0.3)'
              }}
            >
              {loadingMore ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Loading More...
                </>
              ) : (
                <>
                  <i className="bi bi-plus-circle me-2"></i>
                  Load More Reviews
                </>
              )}
            </Button>
          </div>
        )}
      </Container>
    </div>
  );
};

export default ReviewsPage;